import React, { useState } from 'react'
import { useTheme } from '@emotion/react'

import { CommonCardProps } from '../../cards/CardItem.Props'

import {
    StyledPortrait,
    StyledLink,
    StyledCardText,
    StyledCardMedia,
    StyledCardByline,
    StyledFundingLabel,
} from '../../cards/Portrait/Portrait.styled'

import { isLiveSportsTopic, toLinkWithHint } from '@news-mono/web-common'

import { FontScales } from '../../__styling/settings/fontScale'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { themedValue } from '../../__styling/themed-value'
import { GetVideoQueue } from '../../content/Video/PlayerInterface'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { FixedRatio, CardBreakpointRatios } from '@news-mono/web-common'
import { MediaMode } from '../CardMedia/CardMedia'
import { doesParticularRatioExist } from '../CardMedia/image-helpers'
import { isVideoClick } from '../../__helpers/video-card-helpers'
import { createCardClickedEvent } from '@news-mono/web-common'
import {
    PublicationCardItem,
    isPublicationCardItemWithVideo,
} from '@news-mono/web-common'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import { TeaserMode } from '../CardText/CardTeaser'
import {
    applyContainerRatio,
    ResponsiveContainer,
} from '../../content/Picture/responsive'
import { Product } from '@news-mono/web-common'
import { getFundingType } from '../../templates/Publication/lib/get-funding-type'
import { useFeature } from '@news-mono/web-common'
import { KickerMode } from '../Kicker/Kicker'

export interface CardOrientationOptions {
    type: 'Landscape-Portrait'
    maxBreakpoint?: 'sm' | 'md'
}

export interface PortraitState {
    isPlaying?: boolean
}

export interface PortraitProps extends CommonCardProps, ResponsiveContainer {
    hasPadding?: boolean
    hasBackground?: boolean
    hasSeparator?: boolean
    mediaMode?: MediaMode
    hideByline?: boolean
    showProfile?: boolean
    teaserMode: TeaserMode
    kickerMode?: KickerMode

    isLarge?: boolean
    verticalSpacing?: keyof ThemeMargins
    fullWidthOnMobile?: boolean
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    smallImage?: boolean
    fontScale?: FontScales
    isSponsoredCollection?: boolean
    disableImageLazyLoad?: boolean

    timestamp?: TimestampType
    cardNumber: number
    innerRef?: React.RefObject<any>
    cardOrientation?: CardOrientationOptions
    type?: string // Type of card

    adUnitPath?: string
    willPlayVideoInline?: boolean

    item: MaybeLoaded<PublicationCardItem>
    getVideoQueue?: GetVideoQueue
    isInlineRelatedCollection?: boolean
}

export const Portrait: React.FC<PortraitProps> = (props) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const theme = useTheme()

    function linkClicked(e: React.MouseEvent<HTMLElement>) {
        if (!props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }
        const isSevenHeroVideo =
            theme.kind === Product.SevenNews &&
            props.willPlayVideoInline === true
        const exclude = isSevenHeroVideo ? ['vjs-poster'] : []

        if (
            props.willPlayVideoInline &&
            props.fixedRatio &&
            doesParticularRatioExist(props.fixedRatio, '16:9') &&
            isVideoClick(e.nativeEvent.target, exclude)
        ) {
            setIsPlaying(true)
            e.preventDefault()
            return
        } else {
            props.onEvent(
                createCardClickedEvent(
                    props.item.result,
                    'InternalPortrait',
                    'Portrait',
                    props.cardNumber,
                    fundingType,
                    props.isInlineRelatedCollection
                        ? 'InlineRelatedCollection'
                        : undefined,
                ),
            )
        }
    }

    const {
        className,
        hasBackground,
        hasSeparator,
        verticalSpacing,
        isLarge,
        fixedRatio,
        cardOrientation,
        fullWidthOnMobile,
        containerWidthRatios,
        showProfile,
    } = props

    const storyClassification = useFeature('story-classification')

    // SWM-4731 When the page is sponsored, cards should never show the funding status
    const fundingType =
        !props.item.loaded || props.isSponsoredCollection
            ? undefined
            : getFundingType(props.item.result, storyClassification)

    const cardItem = props.item
    const getVideoQueue = !props.item.loaded ? undefined : props.getVideoQueue

    // determine if we are displaying an Opinion Card
    const isOpinionPublication =
        props.item.loaded &&
        props.item.result.primaryTopic.id.indexOf('opinion') > -1
    const isWestOpinionCard = theme.kind === 'thewest' && isOpinionPublication

    const showBylineProfile = themedValue(theme, {
        thewest: isWestOpinionCard && showProfile !== false,
        fallback: showProfile,
    })

    const fontScale = themedValue(theme, {
        thewest: props.fontScale || 0.88,
        perthnow: props.fontScale,
        sevennews: props.fontScale || 1.2,
    }) as FontScales

    const LandscapePortraitCard =
        cardOrientation && cardOrientation.type === 'Landscape-Portrait'

    const isSevenHeroVideo =
        theme.kind === Product.SevenNews && props.willPlayVideoInline === true

    const hidePerthNowKicker =
        theme.kind === Product.PerthNow &&
        props.willPlayVideoInline === true &&
        cardItem.loaded &&
        isPublicationCardItemWithVideo(cardItem.result)

    const playButtonText =
        (isSevenHeroVideo && cardItem.loaded && cardItem.result.kicker) ||
        undefined

    const setKickerState =
        isPlaying || isSevenHeroVideo || hidePerthNowKicker
            ? 'hidden'
            : props.kickerMode

    return (
        <StyledPortrait
            ref={props.innerRef}
            className={className}
            data-topic={
                props.item.loaded
                    ? props.item.result.primaryTopic.id
                    : undefined
            }
            hasBackground={hasBackground}
            hasSeparator={hasSeparator}
            isLarge={isLarge}
            cardOrientation={cardOrientation}
            verticalSpacing={verticalSpacing}
            mediaMode={props.mediaMode}
            fullWidthOnMobile={fullWidthOnMobile}
        >
            <StyledLink
                to={toLinkWithHint(props.item)}
                onClick={linkClicked}
                hasBackground={hasBackground}
                isLarge={isLarge}
                cardOrientation={cardOrientation}
                hasPadding={props.hasPadding}
                fullWidthOnMobile={fullWidthOnMobile}
                isFunded={!!fundingType}
            >
                {props.mediaMode !== 'hidden' && (
                    <StyledCardMedia
                        item={cardItem}
                        fixedRatio={
                            LandscapePortraitCard
                                ? { default: '16:9', sm: '4:3' }
                                : fixedRatio
                        }
                        hasBackground={hasBackground}
                        imageWidths={applyContainerRatio(containerWidthRatios, {
                            mobile: '100vw',
                            tablet: '100vw',
                            desktop: `${theme.siteMetrics.mainContentWidth}px`,
                            fallbackWidth: theme.siteMetrics.mainContentWidth,
                        })}
                        onEvent={props.onEvent}
                        disableImageLazyLoad={props.disableImageLazyLoad}
                        isLarge={isLarge}
                        cardOrientation={cardOrientation}
                        getVideoQueue={getVideoQueue}
                        willPlayVideoInline={props.willPlayVideoInline}
                        adUnitPath={props.adUnitPath}
                        mediaMode={props.mediaMode}
                        fullWidthOnMobile={fullWidthOnMobile}
                        playButtonText={playButtonText}
                    />
                )}
                <StyledCardText
                    item={cardItem}
                    hasBackground={hasBackground}
                    teaserMode={props.teaserMode}
                    hideByline={props.hideByline}
                    insideSponsoredCollection={props.isSponsoredCollection} // ITSD-49300
                    isLiveSportsTopic={
                        props.item.loaded &&
                        props.item.result &&
                        isLiveSportsTopic(props.item.result.primaryTopic.id)
                    }
                    kickerMode={setKickerState}
                    fontScale={fontScale}
                    onEvent={props.onEvent}
                    hasPadding={props.hasPadding}
                    isLarge={isLarge}
                    timestamp={props.timestamp}
                    cardOrientation={cardOrientation}
                    innerRef={undefined}
                    disableImageLazyLoad={undefined}
                    className={undefined}
                />

                {!props.hideByline &&
                    !(fundingType && theme.kind === 'perthnow') && (
                        <StyledCardByline
                            item={cardItem}
                            onEvent={props.onEvent}
                            showProfile={showBylineProfile}
                            isEdgeToEdgeCard={isWestOpinionCard}
                            cardOrientation={cardOrientation}
                            mediaMode={props.mediaMode}
                            showFundingLabel={!!fundingType}
                        />
                    )}
                {fundingType && (
                    <StyledFundingLabel
                        source={
                            theme.kind === 'perthnow' && cardItem.loaded
                                ? cardItem.result.byline
                                : undefined
                        }
                        fundingType={fundingType}
                        hasBackground={hasBackground}
                    />
                )}
            </StyledLink>
        </StyledPortrait>
    )
}
Portrait.displayName = 'Portrait'
